import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface VendorLicense {
  id: string;
  company_id: string;
  vendor_id: string;
  license_type: string | null;
  license_number: string;
  issuing_state: string | null;
  issuing_authority: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  document_path: string | null;
  document_name: string | null;
  uploaded_at: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
}

export interface CreateLicenseInput {
  vendor_id: string;
  license_type?: string;
  license_number: string;
  issuing_state?: string;
  issuing_authority?: string;
  effective_date?: string;
  expiry_date?: string;
  is_primary?: boolean;
  notes?: string;
}

export const useVendorLicenses = (vendorId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["vendor_licenses", vendorId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("vendor_licenses") as any)
        .select("*")
        .eq("vendor_id", vendorId!)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as VendorLicense[];
    },
    enabled: !!user && !!vendorId,
  });
};

export const useCreateVendorLicense = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateLicenseInput) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;
      const { data, error } = await (supabase
        .from("vendor_licenses") as any)
        .insert({
          company_id: companyId,
          vendor_id: input.vendor_id,
          license_type: input.license_type || null,
          license_number: input.license_number,
          issuing_state: input.issuing_state || null,
          issuing_authority: input.issuing_authority || null,
          effective_date: input.effective_date || null,
          expiry_date: input.expiry_date || null,
          is_primary: input.is_primary ?? false,
          notes: input.notes || null,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["vendor_licenses", vars.vendor_id] });
    },
  });
};

export const useUploadLicenseDoc = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ licenseId, vendorId, file }: { licenseId: string; vendorId: string; file: File }) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      const filePath = `${companyId}/${vendorId}/licenses/${Date.now()}_${file.name}`;
      const { error: uploadErr } = await supabase.storage.from("vendor-docs").upload(filePath, file);
      if (uploadErr) throw uploadErr;
      const { error } = await (supabase
        .from("vendor_licenses") as any)
        .update({
          document_path: filePath,
          document_name: file.name,
          uploaded_at: new Date().toISOString(),
        })
        .eq("id", licenseId);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["vendor_licenses", vars.vendorId] });
    },
  });
};
